import { useCallback, useEffect, useState } from "react";
import { Clock, Loader2 } from "lucide-react";
import { toast } from "sonner";

import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { DateField, isoDay } from "@/components/common/DateField";
import { api, apiError } from "@/lib/api";

const F = ({ label, children, hint }) => (
  <div className="space-y-1.5">
    <Label className="text-[11px] text-muted-foreground">{label}</Label>
    {children}
    {hint && <p className="text-[10.5px] text-muted-foreground/80">{hint}</p>}
  </div>
);

/**
 * The one place hours are logged, wherever it is opened from — a volunteer's record, a work item
 * or a hall booking. Everything writes the same time entry.
 */
export const LogTimeDialog = ({ open, onOpenChange, onLogged, fixed = {}, title = "Log time" }) => {
  const [meta, setMeta] = useState(null);
  const [form, setForm] = useState({
    person_kind: "volunteer", person_id: "", activity: "general", reference: "",
    date: isoDay(new Date()), hours: 2, note: "",
  });
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    const res = await api.get("/time/meta");
    setMeta(res.data);
  }, []);

  useEffect(() => {
    if (!open) return;
    load().catch((e) => toast.error(apiError(e)));
    setForm((f) => ({ ...f, ...fixed, date: isoDay(new Date()) }));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, load, JSON.stringify(fixed)]);

  const options = () => {
    if (!meta) return [];
    if (form.activity === "booking") return meta.bookings;
    if (form.activity === "programme") return meta.programmes;
    if (form.activity === "event") return meta.work_items.filter((w) => w.kind === "event");
    if (form.activity === "task") return meta.work_items;
    if (form.activity === "kitchen") return meta.menus || [];
    return [];
  };

  const submit = async () => {
    setBusy(true);
    try {
      const res = await api.post("/time/entries", { ...form, hours: Number(form.hours) });
      toast.success(`${res.data.hours} hours logged for ${res.data.person_name} — awaiting approval`);
      onLogged?.(res.data);
      onOpenChange(false);
      setForm((f) => ({ ...f, hours: 2, note: "" }));
    } catch (e) { toast.error(apiError(e)); }
    setBusy(false);
  };

  const people = (meta?.people || []).filter((p) => p.kind === form.person_kind);
  const needsReference = !["general", "kitchen"].includes(form.activity);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg border-border bg-card" data-testid="log-time-dialog">
        <DialogHeader>
          <DialogTitle>{title}</DialogTitle>
          <DialogDescription className="text-[11.5px] text-muted-foreground">
            Hours land in the shared timesheet as <em>recorded</em>; the volunteer coordinator
            approves them and only approved hours count towards recognition.
          </DialogDescription>
        </DialogHeader>
        <div className="grid grid-cols-2 gap-3">
          <F label="Who">
            <Select value={form.person_kind}
                    onValueChange={(v) => setForm({ ...form, person_kind: v, person_id: "" })}>
              <SelectTrigger data-testid="time-person-kind" className="bg-muted/40">
                <SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="volunteer">A volunteer</SelectItem>
                <SelectItem value="staff">A staff member</SelectItem>
              </SelectContent>
            </Select>
          </F>
          <F label="Name">
            <Select value={form.person_id} onValueChange={(v) => setForm({ ...form, person_id: v })}>
              <SelectTrigger data-testid="time-person-select" className="bg-muted/40">
                <SelectValue placeholder="Pick a person" /></SelectTrigger>
              <SelectContent className="max-h-64">
                {people.map((p) => (
                  <SelectItem key={p.id} value={p.id}>{p.name}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </F>
          <F label="Spent on">
            <Select value={form.activity}
                    onValueChange={(v) => setForm({ ...form, activity: v, reference: "" })}>
              <SelectTrigger data-testid="time-activity-select" className="bg-muted/40">
                <SelectValue /></SelectTrigger>
              <SelectContent>
                {(meta?.activities || []).map((a) => (
                  <SelectItem key={a.key} value={a.key}>{a.label}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </F>
          <F label="Which one">
            <Select value={form.reference}
                    disabled={!needsReference && form.activity !== "kitchen"}
                    onValueChange={(v) => setForm({ ...form, reference: v })}>
              <SelectTrigger data-testid="time-reference-select" className="bg-muted/40">
                <SelectValue placeholder={needsReference ? "Pick one"
                  : form.activity === "kitchen" ? "A menu, if it was for one" : "Not needed"} />
              </SelectTrigger>
              <SelectContent className="max-h-64">
                {options().map((o) => (
                  <SelectItem key={o.reference} value={o.reference}>{o.label}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </F>
          <F label="Date">
            <DateField value={form.date} testId="time-date-input" clearable={false}
                       onChange={(v) => setForm({ ...form, date: v })}
                       className="h-9 w-full text-[11.5px]" />
          </F>
          <F label="Hours" hint="Up to 24 hours a day, in quarter-hour steps.">
            <Input type="number" step="0.25" value={form.hours} data-testid="time-hours-input"
                   onChange={(e) => setForm({ ...form, hours: e.target.value })}
                   className="bg-muted/40 font-mono" />
          </F>
          <div className="col-span-2">
            <F label="What was done">
              <Textarea rows={2} value={form.note} data-testid="time-note-input"
                        onChange={(e) => setForm({ ...form, note: e.target.value })}
                        className="bg-muted/40 text-[12px]" />
            </F>
          </div>
        </div>
        <Button data-testid="time-save-button" onClick={submit}
                disabled={busy || !form.person_id || !form.hours
                  || (needsReference && !form.reference)}
                className="h-9 rounded-full bg-primary text-primary-foreground hover:bg-primary/90">
          {busy ? <Loader2 className="mr-1.5 h-3.5 w-3.5 animate-spin" />
            : <Clock className="mr-1.5 h-3.5 w-3.5" />} Log these hours
        </Button>
      </DialogContent>
    </Dialog>
  );
};
